/**
 * Lightweight client-side validators for the structured PG mailing /
 * physical address sub-form (street, city, state, zip).
 *
 * The Professional Guardian record stores `mailing_address` and
 * `physical_address` as a single comma-joined string in the legacy DB
 * column. The Create form (and now the Edit form) feeds 4 inputs into
 * `formatAddress()` which joins them as `"street, city, state, zip"`.
 * If any of those 4 inputs is empty/garbage, `formatAddress()` will still
 * happily emit a malformed string like `", , , "` or `", , , 33773"`,
 * which then breaks every downstream consumer (bond-creation parser,
 * bond-document templates, billing display, etc.). This helper is the
 * preventive guardrail that stops bad rows from being created in the
 * first place.
 *
 * Usage:
 *   const errors = validateAddressFields(formValues.mailing_address);
 *   if (Object.keys(errors).length) { ... }
 */

import { US_STATE_ABBR_TO_NAME } from "./usStates";

/** Set of valid full state names (lowercased) for case-insensitive lookup. */
const VALID_STATE_NAMES_LOWER = new Set(
  Object.values(US_STATE_ABBR_TO_NAME).map((s) => s.toLowerCase())
);

/** Set of valid 2-letter state abbreviations. */
const VALID_STATE_ABBRS = new Set(Object.keys(US_STATE_ABBR_TO_NAME));

/** Strips trailing commas/spaces/tabs from `str` (regex-free, no backtracking risk). */
const trimTrailingSeparators = (str) => {
  let end = str.length;
  while (end > 0 && (str[end - 1] === "," || str[end - 1] === " " || str[end - 1] === "\t")) {
    end -= 1;
  }
  return str.slice(0, end);
};

/** US ZIP regex - 5 digits, optionally `-` plus 4 more (ZIP+4). */
const ZIP_RE = /^\d{5}(-\d{4})?$/;

/**
 * `react-input-mask` with mask `99999-9999` and `maskChar=""` often leaves a
 * lone trailing hyphen when only five digits are entered (e.g. `33982-`).
 * That value fails ZIP_RE and blocks PG saves after Google prepopulate.
 *
 * @param {unknown} zip
 * @returns {string}
 */
export function normalizeZipCodeForForm(zip) {
  if (zip == null) return "";
  const s = String(zip).trim();
  if (/^\d{5}-$/.test(s)) return s.slice(0, 5);
  return s;
}

/**
 * Validate the 4-piece address sub-form.
 *
 * @param {{street_address?: string, city?: string, state?: string, zipCode?: string}} addr
 * @param {{ requireAll?: boolean, fieldPrefix?: string }} [opts]
 *   - `requireAll` (default true): if true, every field must be non-empty.
 *     Set to false if you want only format-validation (e.g. to allow a
 *     completely-empty optional Physical Address).
 *   - `fieldPrefix`: when set, error keys are prefixed (e.g. "mailing_address.")
 *     so callers can merge errors from multiple address sub-forms into one
 *     flat error map.
 * @returns {Object<string,string>} map of field-key -> error message. Empty
 *   when the sub-form is valid.
 */
export function validateAddressFields(addr, opts = {}) {
  const { requireAll = true, fieldPrefix = "" } = opts;
  const errors = {};
  const street = (addr?.street_address ?? "").toString().trim();
  const city = (addr?.city ?? "").toString().trim();
  const state = (addr?.state ?? "").toString().trim();
  const zip = normalizeZipCodeForForm((addr?.zipCode ?? "").toString().trim());

  // If everything's empty and we don't require it, this is a valid "skipped"
  // address (used for optional Physical Address).
  if (!requireAll && !street && !city && !state && !zip) {
    return errors;
  }

  const k = (name) => `${fieldPrefix}${name}`;

  if (!street) errors[k("street_address")] = "Street Address is required";
  if (!city) errors[k("city")] = "City is required";

  if (!state) {
    errors[k("state")] = "State is required";
  } else {
    const upper = state.toUpperCase();
    const lower = state.toLowerCase();
    if (!VALID_STATE_ABBRS.has(upper) && !VALID_STATE_NAMES_LOWER.has(lower)) {
      errors[k("state")] =
        "State must be a valid US state name (e.g. Florida) or 2-letter abbreviation (e.g. FL)";
    }
  }

  if (!zip) {
    errors[k("zipCode")] = "ZIP/Postal Code is required";
  } else if (!ZIP_RE.test(zip)) {
    errors[k("zipCode")] =
      "ZIP must be 5 digits (e.g. 33773) or ZIP+4 (e.g. 33773-1234)";
  }

  return errors;
}

/**
 * Convenience: returns true iff the address has all 4 components and they're
 * structurally valid (state in the lookup, zip matches the format).
 */
export function isAddressValid(addr) {
  return Object.keys(validateAddressFields(addr)).length === 0;
}

// ---------------------------------------------------------------------------
// Read-side: split the legacy single-string column back into 4 fields.
// Mirrors backend `common_utils.address_parser.parse_us_address` so the PG
// Edit form can hydrate its 4 inputs from `mailing_address` / `physical_address`.
// ---------------------------------------------------------------------------

const ZIP_TRAILING_RE = /\b(\d{5})(?:-?(\d{4}))?\s*$/;

function findStateInToken(token) {
  if (!token) return null;
  const cleaned = token.trim();
  const upper = cleaned.toUpperCase();
  if (VALID_STATE_ABBRS.has(upper)) return US_STATE_ABBR_TO_NAME[upper];
  const lower = cleaned.toLowerCase();
  if (VALID_STATE_NAMES_LOWER.has(lower)) {
    // Return the canonical capitalization from the lookup
    for (const [, name] of Object.entries(US_STATE_ABBR_TO_NAME)) {
      if (name.toLowerCase() === lower) return name;
    }
  }
  return null;
}

/**
 * Robustly parse a legacy `"street, city, state, zip"` string into the
 * 4-field shape used by the structured address sub-form.
 *
 * Returns `{ street_address, city, state, zipCode }` where each value may
 * be an empty string when that piece could not be identified with
 * confidence. The caller can then surface the empty fields as inline
 * validation errors (matches the backend behaviour - we never silently
 * shift wrong values into wrong fields).
 *
 * Examples:
 *   parseAddressString("11417 Harborside Circle, Largo, Florida, 33773")
 *   //  {street_address: "11417 Harborside Circle", city: "Largo",
 *   //   state: "Florida", zipCode: "33773"}
 *
 *   parseAddressString("11417 Harborside Circle, Largo, Florida 33773")
 *   //  same result (3-part input with state+zip merged)
 *
 *   parseAddressString(", , ,") // {street_address: "", city: "", state: "", zipCode: ""}
 */
export function parseAddressString(raw) {
  const empty = { street_address: "", city: "", state: "", zipCode: "" };
  if (raw == null) return empty;
  const text = raw
    .toString()
    .trim()
    .replace(/,$/, "")
    .trim();
  if (!text || !text.replace(/,/g, "").trim()) return empty;

  // 1. Pull off ZIP from the end
  let remainder = text;
  let zipCode = "";
  const zipMatch = ZIP_TRAILING_RE.exec(remainder);
  if (zipMatch) {
    zipCode = zipMatch[2] ? `${zipMatch[1]}-${zipMatch[2]}` : zipMatch[1];
    remainder = trimTrailingSeparators(remainder.slice(0, zipMatch.index));
  }

  // 2. Pull off state from whatever's left
  let state = "";
  const segments = remainder.split(",").map((s) => s.trim());
  if (segments.length) {
    const last = segments[segments.length - 1];
    // Case A: last segment IS the state ("..., Florida")
    const direct = findStateInToken(last);
    if (direct) {
      state = direct;
      segments.pop();
    } else {
      // Case B: state is the trailing word(s) of the last segment
      // ("...Largo Florida")
      const words = last.split(/\s+/);
      let matchedState = null;
      let take = 0;
      for (const t of [3, 2, 1]) {
        if (words.length >= t) {
          const candidate = words.slice(-t).join(" ");
          const found = findStateInToken(candidate);
          if (found) {
            matchedState = found;
            take = t;
            break;
          }
        }
      }
      if (matchedState) {
        state = matchedState;
        const leftover = words.slice(0, -take).join(" ").trim();
        segments[segments.length - 1] = leftover;
        if (!leftover) segments.pop();
      }
    }
  }

  // 3. Whatever remains is "street[, ...], city"
  let street_address = "";
  let city = "";
  const cleanSegments = segments.filter(Boolean);
  if (cleanSegments.length >= 2) {
    city = cleanSegments[cleanSegments.length - 1];
    street_address = cleanSegments.slice(0, -1).join(", ");
  } else if (cleanSegments.length === 1) {
    // Ambiguous single chunk - treat as street, leave city empty so the
    // user sees the inline error and fixes it.
    street_address = cleanSegments[0];
  }

  return { street_address, city, state, zipCode: normalizeZipCodeForForm(zipCode) };
}

/**
 * Build the canonical 4-part comma-joined string for the legacy DB column.
 * Returns ``null`` when any required field is missing or invalid, so we
 * never write garbage like ``", , , 33773"`` into the database.
 *
 * Symmetric with the backend `format_us_address()` helper.
 */
export function formatCanonicalAddress(addr) {
  const zipCode = normalizeZipCodeForForm((addr?.zipCode ?? "").toString().trim());
  const addrForValidation = { ...addr, zipCode };
  if (!isAddressValid(addrForValidation)) return null;
  return [
    addr.street_address.trim(),
    addr.city.trim(),
    addr.state.trim(),
    zipCode,
  ].join(", ");
}

// ---------------------------------------------------------------------------
// Equality + UI option list helpers (shared by Create/Edit screens to drive
// the "Mailing and Physical are the same" toggle and the State dropdown).
// ---------------------------------------------------------------------------

/**
 * Case- and whitespace-insensitive equality check on the legacy
 * single-string mailing/physical address columns. Used on Edit load to
 * derive the initial state of the "addresses are the same" toggle without
 * a new DB column.
 *
 * Treats null/undefined/empty as not-equal-to-anything (so a PG with no
 * physical address on file does NOT auto-toggle on).
 */
export function addressesAreEqual(a, b) {
  const norm = (v) =>
    (v ?? "")
      .toString()
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  const na = norm(a);
  const nb = norm(b);
  if (!na || !nb) return false;
  return na === nb;
}

/**
 * Sorted [{ value: 'FL', label: 'Florida' }, ...] list backing the State
 * Autocomplete in `AddressSubForm`. Sorted by full state name so the
 * dropdown reads alphabetically.
 */
export const STATE_OPTIONS = Object.entries(US_STATE_ABBR_TO_NAME)
  .map(([abbr, name]) => ({ value: abbr, label: name }))
  .sort((x, y) => x.label.localeCompare(y.label));
